import {styled} from "@mui/material/styles";
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {DEFAULT_COMPANY_TABLE_URL} from '../../utils/constant';
import CopyToClipboard from "../CopyToClipboard";
import {StatusChip} from "../Chip/StatusChip";
import Tooltip from '@mui/material/Tooltip';
import SkillChips from "../SkillChip";
import LaunchIcon from '@mui/icons-material/Launch';
import { useNavigate } from 'react-router-dom';

const StyledImg = styled('img')(() => ({
  width: '70px',
  height: '70px'
}));

const FirstRowSpan = styled('span')(() => ({
  maxWidth: '200px',
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'text-top'
}));

const SecondRowSpan = styled('span')(() => ({
  color: '#808080',
  maxWidth: '200px',
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle'
}));

const ImageSecondRowSpan = styled('span')(() => ({
  color: '#808080',
  maxWidth: '270px',
  display: 'inline-block',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle'
}));

export const ImageWithText = (image, firstRow, secondRow) => {
  return (<div className={`${ image ? 'image-text-row' : 'image-text-row'}`}>
      <span className='image-span'>
      {image ? <StyledImg src={image} alt="" border="3" height="80" width="80"/> :
          <StyledImg src={DEFAULT_COMPANY_TABLE_URL} alt="" border="3" height="80" width="80"/>}
    </span>
    <div>
      {
        firstRow && <>
          <span>{firstRow || ""}</span>
          <br />
        </>
      }
      { secondRow && <ImageSecondRowSpan>{secondRow}</ImageSecondRowSpan> }
    </div>
  </div>)
}

export const TableText = (firstRowText, secondRowText, isLink=false, firstTextTooltip="", secondTextTooltip="", isTruncate=false, customStyles={}) => {
  return (
    <div style={{...(customStyles ?? {})}}>
      {
        firstRowText && <>
          <Tooltip title={firstTextTooltip} arrow placement="top-start">
            <span className={`${isTruncate ? 'truncate-2' : ''}`}>{firstRowText}</span>
          </Tooltip>
          <br/>
        </>
      }
      {
        secondRowText && !isLink && <Tooltip title={secondTextTooltip} arrow><SecondRowSpan>{secondRowText || ""}</SecondRowSpan></Tooltip>
      }
      {
        secondRowText && isLink && <Tooltip title={secondTextTooltip} arrow><SecondRowSpan><a href={secondRowText || ""} target='_blank'>{secondRowText || ""}</a></SecondRowSpan></Tooltip>
      }
    </div>
  )
}

export const TableTextWithLink = (launchURl, firstRowText, secondRowText, isLink=false, tooltipText="Copy Link") => {
  const navigate = useNavigate();
  return (
    <div className='copy-text-row'>
      <Tooltip title={tooltipText} arrow placement="top-start">
          <span className='image-span'>
            <LaunchIcon onClick={()=>{
              navigate(launchURl)
            }}/>
          </span>
      </Tooltip>
      <div>
        {
          firstRowText && <>
            <FirstRowSpan>{firstRowText}</FirstRowSpan>
            <br />
          </>
        }
        {
          secondRowText && !isLink && <SecondRowSpan>{secondRowText || ""}</SecondRowSpan>
        }
        {
          secondRowText && isLink && <SecondRowSpan><a href={secondRowText || ""} target='_blank'>{secondRowText || ""}</a></SecondRowSpan>
        }
      </div>
    </div>
  )
}

export const TableTextWithCopy = (copyUrl, firstRowText, secondRowText, isLink=false, tooltipText="Copy Link") => {
  return (
    <div className='copy-text-row'>
        <Tooltip title={tooltipText} arrow placement="top-start">
          <span className='image-span'>
            <CopyToClipboard text={copyUrl}/>
          </span>
        </Tooltip>
      <div>
        {
          firstRowText && <>
            <FirstRowSpan>{firstRowText}</FirstRowSpan>
            <br />
          </>
        }
        {
          secondRowText && !isLink && <SecondRowSpan>{secondRowText || ""}</SecondRowSpan>
        }
        {
          secondRowText && isLink && <SecondRowSpan><a href={secondRowText || ""} target='_blank'>{secondRowText || ""}</a></SecondRowSpan>
        }
      </div>
    </div>
  )
}

export const LinearProgressWithLabel = (props) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress
          sx={{
            backgroundColor: `${props?.value < 40 ? '#ffe6e6' : props?.value >= 40 && props?.value < 70 ? '#fff4e6' : props?.value >= 70 ? '#e0edde' : ''}`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: `${props?.value < 40 ? '#F24C3D' : props?.value >= 40 && props?.value < 70 ? '#F2BE22' : props?.value >= 70 ? '#22A699' : ''}`
            }
          }}
          style={{ borderRadius: 10, height: '0.3rem'}}
          variant="determinate"
          {...props}
        />
      </Box>
      {
        props?.showValue ?
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="text.secondary">{`${props?.value ? `${props?.value}%`: '0%'}`}</Typography>
          </Box>
        : ''
      }
    </Box>
  );
}

export const TableStatusWithText = (statusText, statusColor, firstRowText, progressValue) => {
  return (
    <div>
      <span>
        <StatusChip label={statusText} color={`${statusColor ? statusColor : 'default'}`} />
        <br/>
      </span>
      {
        firstRowText && <Box mt={1}><span>{firstRowText}</span></Box>
      }
      {
        progressValue ?
          <Box sx={{ width: '100%' }} mt={1}>
            <Tooltip title={`${progressValue ? `${progressValue || 0}%`: '0%'}`} arrow placement="top-start">
            <LinearProgressWithLabel value={progressValue || 0} showValue={true} />
            </Tooltip>
          </Box>
        : null
      }
    </div>
  )
}

export const TableSkills = (skills, skillTooltip="", isIcon=false, isTruncate=false) => {
  const returnElement = (<Tooltip title={skillTooltip || ""} arrow placement="top-start">
    <div className={`${isTruncate ? 'truncate-chip' : ''}`}>
      {
        skills?.length ?
          (skills || [])?.map(skillName => {
            return (<SkillChips skill={{ name: skillName }} isIcon={isIcon} size="small" style={{ marginBottom: '4px', marginRight: '4px' }} />)
          })
        : <span>Skills are not available.</span>
      }
    </div>
  </Tooltip>)
  return returnElement
}
